import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle } from 'lucide-react';
import { cn } from './cn.js';

export function Card({ className, children, ...props }) {
  return (
    <div className={cn('rounded-xl bg-white shadow-xs ring-1 ring-slate-200', className)} {...props}>
      {children}
    </div>
  );
}

export function CardHeader({ title, description, actions, className }) {
  return (
    <div
      className={cn('flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4', className)}
    >
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function CardBody({ className, children }) {
  return <div className={cn('px-5 py-4', className)}>{children}</div>;
}

const TONES = {
  neutral: 'bg-slate-100 text-slate-700 ring-slate-200',
  brand: 'bg-brand-50 text-brand-700 ring-brand-200',
  success: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  warning: 'bg-amber-50 text-amber-800 ring-amber-200',
  danger: 'bg-red-50 text-red-700 ring-red-200',
  info: 'bg-sky-50 text-sky-700 ring-sky-200',
};

export function Badge({ tone = 'neutral', className, children }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

const ALERT = {
  info: { cls: 'bg-sky-50 text-sky-900 ring-sky-200', Icon: Info },
  success: { cls: 'bg-emerald-50 text-emerald-900 ring-emerald-200', Icon: CheckCircle2 },
  warning: { cls: 'bg-amber-50 text-amber-900 ring-amber-200', Icon: AlertTriangle },
  danger: { cls: 'bg-red-50 text-red-900 ring-red-200', Icon: XCircle },
};

export function Alert({ tone = 'info', title, children, action, className }) {
  const { cls, Icon } = ALERT[tone];
  return (
    <div
      className={cn('flex gap-3 rounded-lg p-3 text-sm ring-1 ring-inset', cls, className)}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(title && 'mt-0.5')}>{children}</div>}
      </div>
      {action}
    </div>
  );
}

export function Spinner({ className }) {
  return <Loader2 className={cn('size-5 animate-spin text-slate-400', className)} aria-label="Loading" />;
}

export function PageLoader({ label = 'Loading…' }) {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-sm text-slate-500">
      <Spinner className="size-7" />
      {label}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      {Icon && (
        <div className="mb-3 rounded-full bg-slate-100 p-3">
          <Icon className="size-6 text-slate-500" aria-hidden />
        </div>
      )}
      <p className="font-medium text-slate-900">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-slate-500">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function StatCard({ label, value, hint, tone = 'neutral', icon: Icon, onClick }) {
  const toneCls = {
    neutral: 'text-slate-900',
    success: 'text-emerald-700',
    warning: 'text-amber-700',
    danger: 'text-red-700',
    brand: 'text-brand-700',
  }[tone];
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp
      onClick={onClick}
      className={cn(
        'rounded-xl bg-white p-4 text-left shadow-xs ring-1 ring-slate-200',
        onClick && 'transition hover:ring-brand-300 focus-visible:outline-2 focus-visible:outline-brand-600',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-slate-500">{label}</p>
        {Icon && <Icon className="size-4 text-slate-400" aria-hidden />}
      </div>
      <p className={cn('mt-1 text-2xl font-semibold tabular-nums', toneCls)}>{value ?? '—'}</p>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
    </Comp>
  );
}

/** Two-column label/value list. items: [[label, value], ...] */
export function DescriptionList({ items, className }) {
  return (
    <dl className={cn('grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2', className)}>
      {items.filter(Boolean).map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="text-slate-500">{label}</dt>
          <dd className="mt-0.5 break-words font-medium text-slate-900">{value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}
